import {Component, EventEmitter, Input, Output} from '@angular/core';
import {SmNavMenuItemModel} from '../sm-nav-menu-item/sm-nav-menu-item.component';
import {NavigationGroupPreferencesService} from '../../navigation/navigation-group-preferences.service';

export type SmNavBarProfileModel = {
  email: string;
  roleLabel: string;
  avatarText?: string;
};

@Component({
  selector: 'sm-nav-bar',
  templateUrl: './sm-nav-bar.component.html',
  styleUrls: ['./sm-nav-bar.component.scss'],
  standalone: false,
})
export class SmNavBarComponent {
  @Input() profile?: SmNavBarProfileModel;
  private _items: SmNavMenuItemModel[] = [];
  private _selectedId = '';
  private _preferenceAccountId = '';
  private loadedPreferenceAccountId: string | null = null;
  private initializationId = 0;
  private storedGroupStates: Record<string, boolean> = {};

  @Input()
  set items(value: SmNavMenuItemModel[]) {
    this._items = value ?? [];
    this.initializeOrMergeGroupStates();
  }

  get items(): SmNavMenuItemModel[] {
    return this._items;
  }

  @Input()
  set selectedId(value: string) {
    this._selectedId = value ?? '';
    this.initializeOrMergeGroupStates();
  }

  get selectedId(): string {
    return this._selectedId;
  }

  @Input()
  set preferenceAccountId(value: string) {
    const nextAccountId = value ?? '';
    if (this._preferenceAccountId === nextAccountId) {
      return;
    }

    this._preferenceAccountId = nextAccountId;
    this.loadedPreferenceAccountId = null;
    this.storedGroupStates = {};
    this.expandedItemIds.clear();
    this.stateReady = false;
    this.initializeOrMergeGroupStates();
  }

  get preferenceAccountId(): string {
    return this._preferenceAccountId;
  }

  @Input() footerText = '';
  @Input() footerActionText = '';

  @Output() itemSelected = new EventEmitter<SmNavMenuItemModel>();
  @Output() footerActionClick = new EventEmitter<void>();

  readonly expandedItemIds = new Set<string>();
  stateReady = false;

  constructor(private readonly navigationGroupPreferences: NavigationGroupPreferencesService) {}

  isExpanded(itemId: string): boolean {
    return this.expandedItemIds.has(itemId);
  }

  isSelected(item: SmNavMenuItemModel): boolean {
    if (item.children?.length) {
      return this.hasSelectedDescendant(item);
    }

    return this.selectedId === item.id;
  }

  isCurrent(item: SmNavMenuItemModel): boolean {
    return this.selectedId === item.id && !item.children?.length;
  }

  handleItemClick(item: SmNavMenuItemModel): void {
    if (item.disabled) {
      return;
    }

    if (item.children?.length) {
      this.toggleExpanded(item.id);
      return;
    }

    if (!item.externalHref) {
      this.selectedId = item.id;
    }
    this.itemSelected.emit(item);
  }

  handleFooterActionClick(): void {
    this.footerActionClick.emit();
  }

  trackById(_: number, item: SmNavMenuItemModel): string {
    return item.id;
  }

  private toggleExpanded(itemId: string): void {
    if (this.expandedItemIds.has(itemId)) {
      this.expandedItemIds.delete(itemId);
    } else {
      this.expandedItemIds.add(itemId);
    }

    this.storedGroupStates[itemId] = this.expandedItemIds.has(itemId);
    void this.navigationGroupPreferences.write(this.preferenceAccountId, this.storedGroupStates);
  }

  private hasSelectedDescendant(item: SmNavMenuItemModel): boolean {
    return Boolean(item.children?.some((child) => this.isSelected(child)));
  }

  private initializeOrMergeGroupStates(): void {
    if (!this.items.length) {
      return;
    }

    if (!this.preferenceAccountId) {
      this.loadedPreferenceAccountId = '';
      this.applyGroupStates();
      this.stateReady = true;
      return;
    }

    if (this.loadedPreferenceAccountId === this.preferenceAccountId) {
      this.applyGroupStates();
      return;
    }

    const initializationId = ++this.initializationId;
    const accountId = this.preferenceAccountId;
    void this.navigationGroupPreferences.read(accountId).then((storedGroupStates) => {
      if (initializationId !== this.initializationId || accountId !== this.preferenceAccountId) {
        return;
      }

      this.storedGroupStates = storedGroupStates ?? {};
      this.loadedPreferenceAccountId = accountId;
      this.applyGroupStates();
      this.stateReady = true;
    });
  }

  private applyGroupStates(): void {
    const nextExpandedIds = new Set<string>();

    for (const group of this.items.filter((item) => item.children?.length)) {
      const hasStoredState = Object.prototype.hasOwnProperty.call(this.storedGroupStates, group.id);
      const expanded = hasStoredState
        ? this.storedGroupStates[group.id]
        : this.hasSelectedDescendant(group);

      if (!hasStoredState && this.preferenceAccountId) {
        this.storedGroupStates[group.id] = expanded;
      }

      if (expanded) {
        nextExpandedIds.add(group.id);
      }
    }

    this.expandedItemIds.clear();
    nextExpandedIds.forEach((groupId) => this.expandedItemIds.add(groupId));
  }
}
